import { useEffect, useRef, useState } from 'react';
import { registerDrawnMolecule, type DrawnMolecule } from '@peruselab/api-client/molecules';
import { registerDrawnIsomer } from '@peruselab/api-client/isomers';
import { toChemMoleculeV1 } from '@peruselab/structure-editor/domain/moleculeProjection';
import { isomerSourceKey, toIsomerCreatePayload } from '@peruselab/structure-editor/domain/moleculeDrawingProjection';
import { drawnHydrogenIds, remapStructureIds } from '@peruselab/structure-editor/domain/structureIdRemap';
import type { DrawingDocument } from '../generated/drawing-contract';
import { queryMolecules, type QueryMolecule } from './queryMolecules';

/**
 * #1064 (ADR 0019 D5, O4, O5): each molecule on the paper is registered for the user's space once
 * it is drawn -- after each settled edit, when the chemistry check has caught up -- so "first in
 * PeruseLab" is when the work was done, not when it is saved. One request per structure and space
 * for the session. #1066 step 3: then its Isomer (its stereo, onto the Molecule's atoms as a save
 * puts it), once per stereo arrangement -- a changed wedge registers again. Skipped: fragments the
 * check flags, and anything when `enabled` is false (signed out, a view-only role, Markush mode).
 *
 * @param checked the atoms and bonds the chemistry check flagged in the current drawing; null while
 *     it's still checking it (or couldn't).
 * @returns how many structures are waiting for PubChem (queued on the server, D8), and how many
 *     structures new to PeruseLab have been registered so far -- #1103: the drawing's search asks
 *     again then (it ran before they existed), as #1101's structure check does (the caller has
 *     just become their author).
 */
export function useDrawnMoleculeRegistration(
  document: DrawingDocument,
  checked: { readonly invalidAtomIds: ReadonlySet<string>; readonly invalidBondIds: ReadonlySet<string> } | null,
  scopeKey: string,
  enabled: boolean
): { readonly pending: number; readonly registered: number } {
  // Per space: what each structure turned out to be (or that it's being asked).
  const asked = useRef(new Map<string, Promise<DrawnMolecule | null>>());
  const askedIsomers = useRef(new Set<string>());
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(new Set());
  const [registered, setRegistered] = useState(0);

  useEffect(() => {
    if (!enabled || checked === null) return;
    for (const molecule of queryMolecules(document)) {
      if (!registrable(molecule, checked.invalidAtomIds, checked.invalidBondIds)) continue;
      const key = `${scopeKey}|${molecule.key}`;
      const isomerKey = `${key}|${isomerSourceKey(molecule.document)}`;
      const known = asked.current.get(key);
      if (known) {
        // The molecule is known; a new stereo arrangement of it is a new Isomer to register.
        if (!askedIsomers.current.has(isomerKey)) {
          askedIsomers.current.add(isomerKey);
          void known.then((drawn) => registerIsomer(molecule, drawn, () => askedIsomers.current.delete(isomerKey)));
        }
        continue;
      }
      const answer = registerDrawnMolecule(toChemMoleculeV1(molecule.document)).catch(() => null);
      asked.current.set(key, answer);
      askedIsomers.current.add(isomerKey);
      void answer.then((drawn) => registerIsomer(molecule, drawn, () => askedIsomers.current.delete(isomerKey)));
      void answer.then((drawn) => {
        if (drawn === null)
          asked.current.delete(key); // asked again after the next edit
        // #1103: only a structure new to PeruseLab couldn't have been found by searches before it.
        else if (drawn.found === 'new') setRegistered((count) => count + 1);
        setPendingKeys((current) => {
          const next = new Set(current);
          if (drawn?.found === 'pending') next.add(key);
          else next.delete(key);
          return next;
        });
      });
    }
  }, [document, checked, scopeKey, enabled]);

  // Only what's on the paper now, in this space, counts as waiting.
  const onPaper = new Set(queryMolecules(document).map((molecule) => `${scopeKey}|${molecule.key}`));
  return { pending: [...pendingKeys].filter((key) => onPaper.has(key)).length, registered };
}

/**
 * #1066 step 3: the Isomer of a molecule just registered, as a save would send it -- its stereo on the
 * Molecule's own atoms (#615), a drawn hydrogen the Molecule holds as implicit named as that (#951).
 * Best effort: a failure is asked again after the next edit (`retry`), and changes nothing on screen.
 */
function registerIsomer(molecule: QueryMolecule, drawn: DrawnMolecule | null, retry: () => void): void {
  if (!drawn?.id) {
    retry();
    return;
  }
  try {
    const { correspondence } = drawn;
    const aligned = correspondence ? remapStructureIds(molecule.document, correspondence) : molecule.document;
    const hydrogens = correspondence ? drawnHydrogenIds(aligned, correspondence) : {};
    registerDrawnIsomer(toIsomerCreatePayload(aligned, drawn.id, drawn.documentRevision ?? 1, hydrogens)).catch(retry);
  } catch {
    retry();
  }
}

/** O4: one valid molecule -- none of its atoms or bonds flagged by the chemistry check. */
function registrable(
  molecule: QueryMolecule,
  invalidAtomIds: ReadonlySet<string>,
  invalidBondIds: ReadonlySet<string>
): boolean {
  const { graph } = molecule.document.structure;
  return (
    graph.nodes.length > 0 &&
    !graph.nodes.some((node) => invalidAtomIds.has(node.id)) &&
    !graph.edges.some((edge) => invalidBondIds.has(edge.id))
  );
}
